import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Settings, Cloud, CloudOff, Download, Upload, Trash2, X, Copy, Palette, Grid, FolderHeart, Sparkles } from 'lucide-react';
import classNames from '../helpers/classNames';
import { useAppContext } from '../contexts/AppContext';
import { useI18n } from '../contexts/I18nContext';
import { useAuth, setOnLoginSuccess } from '../contexts/AuthContext';
import ColorSwatch from '../components/ColorSwatch';
import MarkerSearchModal from '../components/MarkerSearchModal';
import PageHelpModal from '../components/PageHelpModal';
import SyncStatusIndicator from '../components/SyncStatusIndicator';
import CollectionConflictModal from '../components/CollectionConflictModal';
import { useConfirmModal } from '../components/ConfirmModal';
import {
  getUserCollection,
  saveUserCollection,
  addMarkerToCollection,
  removeMarkerFromCollection,
  clearCollection,
  exportCollectionAsJSON,
  importCollectionFromJSON,
  searchCollection,
  reconcileCollectionHex
} from '../utils/userCollection';
import { markers as sourceMarkers } from '../utils/data';
import {
  hasCloudBackup,
  downloadBackupKeyFile,
  copyBackupKeyToClipboard,
  loadFromCloud,
  disconnectFromCloud,
  saveToCloudWithGoogle,
  loadFromCloudWithGoogle
} from '../config/firebase';
import { getDatabase, ref, get } from 'firebase/database';
import { markExpandCollectionSeen } from '../services/profileService';

export default function MyCollectionPage() {
  const navigate = useNavigate();
  const { brand, ohuhuMode } = useAppContext();
  const { t } = useI18n();
  const { user, login, resolveConflict } = useAuth();
  const { showConfirm, ConfirmModalComponent } = useConfirmModal();

  // Set up login success callback to navigate to collection page
  useEffect(() => {
    setOnLoginSuccess(() => navigate('/collection'));
    return () => setOnLoginSuccess(null);
  }, [navigate]);

  const [collection, setCollection] = useState([]);
  const [groupedCollection, setGroupedCollection] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSettingsMenu, setShowSettingsMenu] = useState(false);
  const [showRestoreMenu, setShowRestoreMenu] = useState(false);
  const [cloudSynced, setCloudSynced] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showContent, setShowContent] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredCollection, setFilteredCollection] = useState([]);

  // Expand nudge
  const [showExpandNudge, setShowExpandNudge] = useState(false);

  useEffect(() => {
    const localSeen = localStorage.getItem('markerSync_expandCollectionSeen') === 'true';
    if (localSeen) return;
    if (user) {
      get(ref(getDatabase(), `users/${user.uid}/expandCollectionSeen`)).then(snap => {
        if (!snap.exists() || !snap.val()) setShowExpandNudge(true);
      }).catch(() => setShowExpandNudge(true));
    } else {
      setShowExpandNudge(true);
    }
  }, [user]);

  const dismissExpandNudge = useCallback(() => {
    setShowExpandNudge(false);
    localStorage.setItem('markerSync_expandCollectionSeen', 'true');
    if (user) markExpandCollectionSeen(user.uid);
  }, [user]);

  // Google Auth sync states
  const [syncStatus, setSyncStatus] = useState('local-only'); // 'synced' | 'syncing' | 'offline' | 'local-only' | 'error'
  const [showConflictModal, setShowConflictModal] = useState(false);
  const [conflictData, setConflictData] = useState(null);
  const [retryInterval, setRetryIntervalState] = useState(null);

  const formatMessage = (template, values = {}) =>
    (template || '').replace(/\{(\w+)\}/g, (_, key) => values[key] ?? '');

  // Load collection on mount and handle sync scenarios
  useEffect(() => {
    const initializeCollection = async () => {
      setIsLoading(true);

      try {
        // Scenario: User is logged in with Google
        if (user) {
          setSyncStatus('syncing');

          // Get local and cloud collections
          const localCollection = JSON.parse(localStorage.getItem('markerCollection') || '[]');
          const hasLocalMarkers = localCollection.length > 0;

          // Load cloud collection
          const cloudResult = await loadFromCloudWithGoogle(user.uid);
          const hasCloudMarkers = cloudResult.success && cloudResult.markers && cloudResult.markers.length > 0;

          // CASE 1: User has local markers AND cloud markers (POTENTIAL CONFLICT)
          if (hasLocalMarkers && hasCloudMarkers) {
            // Check if they're different (compare both size and content)
            const localIds = new Set(localCollection.map(m => m.id || `${m.brand}-${m.code || m.code_new || m.code_old}`));
            const cloudIds = new Set(cloudResult.markers.map(m => m.id || `${m.brand}-${m.code || m.code_new || m.code_old}`));

            // Collections are same if they have same size AND same markers
            const areSame = localIds.size === cloudIds.size &&
                           [...localIds].every(id => cloudIds.has(id)) &&
                           [...cloudIds].every(id => localIds.has(id));

            if (!areSame) {
              // Show conflict modal - user must choose
              setConflictData({
                local: {
                  collection: localCollection,
                  count: localCollection.length,
                  timestamp: localStorage.getItem('markerCollectionTimestamp')
                    ? new Date(parseInt(localStorage.getItem('markerCollectionTimestamp'))).toISOString()
                    : null
                },
                cloud: {
                  collection: cloudResult.markers,
                  count: cloudResult.markers.length,
                  timestamp: cloudResult.updatedAt
                }
              });
              setShowConflictModal(true);
              // Display local collection while waiting for user choice (don't update localStorage yet!)
              await loadAndReconcile(localCollection, { persist: false });
            } else {
              // Collections are the same - use cloud as source of truth
              const reconciled = await loadAndReconcile(cloudResult.markers);
              localStorage.setItem('markerCollection', JSON.stringify(reconciled));
            }
          }
          // CASE 2: User has local markers, NO cloud markers (FIRST SYNC)
          else if (hasLocalMarkers && !hasCloudMarkers) {
            const reconciled = await loadAndReconcile(localCollection);
            // Sync (possibly corrected) collection to cloud
            await syncCollectionToCloud(reconciled);
            // Show simple alert
            alert(formatMessage(t('auth.syncSuccess'), { count: reconciled.length }));
          }
          // CASE 3: User has NO local markers, HAS cloud markers (DOWNLOAD)
          else if (!hasLocalMarkers && hasCloudMarkers) {
            // Download cloud to local
            const reconciled = await loadAndReconcile(cloudResult.markers);
            localStorage.setItem('markerCollection', JSON.stringify(reconciled));
          }
          // CASE 4: User has NO local markers, NO cloud markers (FRESH START)
          else {
            // Empty collection
            setCollection([]);
            localStorage.setItem('markerCollection', JSON.stringify([]));
          }

          // User is now using Google Auth - disconnect from any backup key
          if (hasCloudBackup()) {
            disconnectFromCloud();
          }

          setCloudSynced(true);
          setSyncStatus('synced');
        }
        // Scenario: User is NOT logged in - use existing backup key system
        else {
          setCloudSynced(hasCloudBackup());
          setSyncStatus(hasCloudBackup() ? 'synced' : 'local-only');

          // If cloud backup is enabled, sync from cloud first
          if (hasCloudBackup()) {
            const result = await loadFromCloud();
            if (result.success && result.markers) {
              // Save to localStorage (without triggering another cloud save)
              await saveUserCollection(result.markers, false);
            }
          }

          // Load from localStorage
          loadCollection();
        }
      } catch (error) {
        console.error('Failed to initialize collection:', error);
        setSyncStatus('error');
        // Fall back to local collection
        loadCollection();
      } finally {
        setIsLoading(false);
      }
    };

    initializeCollection();
  }, [user]);

  // Handle conflict resolution
  const handleConflictResolve = async (choice) => {
    if (!conflictData) return;

    const result = await resolveConflict(
      choice,
      conflictData.local.collection,
      conflictData.cloud.collection
    );

    if (result.success) {
      const reconciled = await loadAndReconcile(result.collection);
      localStorage.setItem('markerCollection', JSON.stringify(reconciled));

      // Clear backup key if user is now using Google Auth
      // This prevents old backup key data from interfering when logged out
      if (hasCloudBackup()) {
        disconnectFromCloud();
        setCloudSynced(false);
      }

      setShowConflictModal(false);
      setConflictData(null);
      setSyncStatus('synced');
    }
  };

  // Sync to cloud when collection changes (for Google users)
  const syncCollectionToCloud = useCallback(async (markers, options = {}) => {
    if (!user) return;

    setSyncStatus('syncing');
    try {
      const result = await saveToCloudWithGoogle(markers, user.uid, options);
      if (result.success) {
        setSyncStatus('synced');
        // Clear any retry interval
        if (retryInterval) {
          clearInterval(retryInterval);
          setRetryIntervalState(null);
        }
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Failed to sync to cloud:', error);
      setSyncStatus('offline');
      // Start retry interval
      startRetrySync(markers);
    }
  }, [user, retryInterval]);

  // Reconcile hex values from storage against the current source data, then set state.
  // If anything changed and persist=true, writes the corrected collection to localStorage
  // and (if the user is logged in with Google) syncs to cloud — so the fix is durable
  // and won't repeat on the next load.
  const loadAndReconcile = useCallback(async (rawMarkers, { persist = true } = {}) => {
    const { markers: reconciled, changed } = reconcileCollectionHex(rawMarkers, sourceMarkers);
    setCollection(reconciled);
    if (changed > 0 && persist) {
      await saveUserCollection(reconciled, false); // saves to localStorage only
      if (user) {
        syncCollectionToCloud(reconciled); // push corrected copy to cloud
      }
      console.info(`[MarkerSync] Reconciled ${changed} stale hex value(s) in collection.`);
    }
    return reconciled;
  }, [user, syncCollectionToCloud]);

  // Auto-retry sync when offline
  const startRetrySync = (markers) => {
    if (retryInterval) return; // Already retrying

    const interval = setInterval(async () => {
      if (!user) {
        clearInterval(interval);
        setRetryIntervalState(null);
        return;
      }

      setSyncStatus('syncing');
      try {
        const result = await saveToCloudWithGoogle(markers, user.uid);
        if (result.success) {
          setSyncStatus('synced');
          clearInterval(interval);
          setRetryIntervalState(null);
        }
      } catch (error) {
        setSyncStatus('offline');
      }
    }, 5000); // Retry every 5 seconds

    setRetryIntervalState(interval);
  };

  // Cleanup retry interval on unmount
  useEffect(() => {
    return () => {
      if (retryInterval) {
        clearInterval(retryInterval);
      }
    };
  }, [retryInterval]);

  // Smoothly fade content in after initial load
  useEffect(() => {
    if (!isLoading) {
      const timer = setTimeout(() => setShowContent(true), 80);
      return () => clearTimeout(timer);
    }
    setShowContent(false);
  }, [isLoading]);

  // Update filtered/grouped collection when collection or search changes
  useEffect(() => {
    const sortMarkersByCode = (markers) => [...markers].sort((a, b) => {
      const normalize = (code) => String(code || '').trim().toUpperCase();
      const codeA = normalize(a.code || a.code_new || a.code_old);
      const codeB = normalize(b.code || b.code_new || b.code_old);
      const isNumericA = /^\d+$/.test(codeA);
      const isNumericB = /^\d+$/.test(codeB);
      // Pure-numeric codes come before codes with letters (0, 1, 2... then BG1, CG3, WG0...)
      if (isNumericA !== isNumericB) return isNumericA ? -1 : 1;
      if (isNumericA) return parseInt(codeA, 10) - parseInt(codeB, 10);
      // Both have letters: sort by alpha prefix first (BG, CG, WG...), then by number
      const alphaA = codeA.replace(/\d+/g, '');
      const alphaB = codeB.replace(/\d+/g, '');
      const alphaCmp = alphaA.localeCompare(alphaB);
      if (alphaCmp !== 0) return alphaCmp;
      const numA = parseInt(codeA.match(/\d+/)?.[0] ?? 'Infinity', 10);
      const numB = parseInt(codeB.match(/\d+/)?.[0] ?? 'Infinity', 10);
      return numA - numB;
    });

    const groupAndSort = (markers) => {
      const grouped = {};
      markers.forEach(marker => {
        const brandName = marker.brand || 'Unknown';
        if (!grouped[brandName]) grouped[brandName] = [];
        grouped[brandName].push(marker);
      });
      Object.keys(grouped).forEach(brand => {
        grouped[brand] = sortMarkersByCode(grouped[brand]);
      });
      return grouped;
    };

    if (searchQuery.trim()) {
      const results = searchCollection(searchQuery);
      setFilteredCollection(results);
      setGroupedCollection(groupAndSort(results));
    } else {
      setFilteredCollection([]);
      setGroupedCollection(groupAndSort(collection));
    }
  }, [collection, searchQuery]);

  const loadCollection = () => {
    const markers = getUserCollection();
    loadAndReconcile(markers);
  };

  const handleAddMarker = async (marker) => {
    const result = await addMarkerToCollection(marker);
    if (result.success && result.added) {
      const updatedCollection = getUserCollection();
      setCollection(updatedCollection);
      setShowAddModal(false);

      // Sync to cloud if logged in with Google
      if (user) {
        syncCollectionToCloud(updatedCollection);
      }
    }
  };

  const handleAddMarkersBulk = async (markersToAdd = []) => {
    if (!Array.isArray(markersToAdd) || markersToAdd.length === 0) {
      return;
    }

    let addedCount = 0;
    for (const marker of markersToAdd) {
      const result = await addMarkerToCollection(marker);
      if (result.success && result.added) {
        addedCount += 1;
      }
    }

    if (addedCount > 0) {
      const updatedCollection = getUserCollection();
      setCollection(updatedCollection);
      setShowAddModal(false);

      // Sync to cloud if logged in with Google
      if (user) {
        syncCollectionToCloud(updatedCollection);
      }
    }
  };

  const handleRemoveMarker = async (marker) => {
    const confirmed = await showConfirm({
      title: t('collectionPage.prompts.removeMarkerTitle'),
      message: formatMessage(
        t('collectionPage.prompts.removeMarker'),
        { name: marker.name || marker.code || t('collectionPage.stats.marker') }
      ),
      confirmText: t('collectionPage.buttons.remove'),
      cancelText: t('collectionPage.buttons.cancel'),
      variant: 'danger'
    });

    if (!confirmed) return;

    const result = await removeMarkerFromCollection(marker);
    if (result.success && result.removed) {
      const updatedCollection = getUserCollection();
      setCollection(updatedCollection);

      // Sync to cloud if logged in with Google
      if (user) {
        syncCollectionToCloud(updatedCollection);
      }
    }
  };

  const handleRemoveBrand = async (brandName) => {
    const normalizedBrand = brandName === 'Unknown' ? t('collectionPage.unknownBrand') : brandName;
    const brandMarkers = collection.filter(
      (marker) => (marker.brand || 'Unknown') === brandName
    );

    if (brandMarkers.length === 0) return;

    const confirmed = await showConfirm({
      title: t('collectionPage.prompts.removeBrandTitle'),
      message: formatMessage(
        t('collectionPage.prompts.removeBrand'),
        { brand: normalizedBrand, count: brandMarkers.length }
      ),
      confirmText: t('collectionPage.buttons.remove'),
      cancelText: t('collectionPage.buttons.cancel'),
      variant: 'danger'
    });

    if (!confirmed) return;

    const updatedCollection = collection.filter(
      (marker) => (marker.brand || 'Unknown') !== brandName
    );
    await saveUserCollection(updatedCollection);
    setCollection(updatedCollection);

    if (user) {
      syncCollectionToCloud(updatedCollection);
    }
  };

  const handleClearCollection = async () => {
    const confirmed = await showConfirm({
      title: t('collectionPage.prompts.clearCollectionTitle'),
      message: formatMessage(
        t('collectionPage.prompts.clearCollection'),
        { count: collection.length }
      ),
      confirmText: t('collectionPage.buttons.clearAll'),
      cancelText: t('collectionPage.buttons.cancel'),
      variant: 'danger'
    });

    if (!confirmed) return;

    const result = await clearCollection();
    if (result.success) {
      // Immediately update local state to show empty collection
      setCollection([]);
      setGroupedCollection({});
      setShowSettingsMenu(false);

      // Sync empty collection to cloud if logged in with Google
      if (user) {
        syncCollectionToCloud([]);
      }
    }
  };


  const handleSyncFromCloud = async () => {
    // Always prompt user to enter backup key
    const key = window.prompt(formatMessage(
      t('collectionPage.prompts.restoreBackupKey'),
      { format: 'MKSYNC-XXXXXXXX-XXXX-XXXX-XXXX-XXXX-XXXX-XXXX' }
    ));

    if (!key) {
      return; // User cancelled
    }

    try {
      // Import the backup key temporarily (don't persist it)
      const { importBackupKey, loadFromCloud: loadFromCloudDirect, disconnectFromCloud } = await import('../config/firebase');
      const rawKey = importBackupKey(key.trim());

      // Load from cloud using the provided key
      const result = await loadFromCloudDirect(rawKey);

      if (result.success) {
        // CRITICAL: If user is logged in with Google, sync to their account and disconnect the backup key
        if (user) {
          // Update local state (reconcile stale hex values while we're at it)
          const reconciled = await loadAndReconcile(result.markers);
          localStorage.setItem('markerCollection', JSON.stringify(reconciled));

          // Save to cloud with Google Auth, including migration metadata
          await syncCollectionToCloud(reconciled, { migratedFrom: rawKey });

          // Immediately disconnect the backup key AFTER syncing to prevent dual-sync
          disconnectFromCloud();

          setCloudSynced(false); // No longer using backup key
          alert(formatMessage(t('collectionPage.alerts.restoreSuccess'), { count: result.markers.length }));
        } else {
          // User is NOT logged in - use backup key system
          await saveUserCollection(result.markers);
          loadCollection();
          setCloudSynced(true);
          alert(formatMessage(t('collectionPage.alerts.restoreSuccess'), { count: result.markers.length }));
        }

        setShowSettingsMenu(false);
      } else {
        alert(formatMessage(t('collectionPage.alerts.restoreFailed'), { error: result.error }));
      }
    } catch (error) {
      alert(formatMessage(t('collectionPage.alerts.invalidBackupKey'), { error: error.message }));
    }
  };

  const handleExport = () => {
    exportCollectionAsJSON();
    setShowSettingsMenu(false);
  };

  const handleImport = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      try {
        const result = await importCollectionFromJSON(file);
        if (result.success) {
          loadCollection();
          alert(formatMessage(t('collectionPage.alerts.importSuccess'), { count: result.imported }));
          setShowSettingsMenu(false);
        } else {
          alert(formatMessage(t('collectionPage.alerts.importFailed'), { error: result.error }));
        }
      } catch (error) {
        alert(formatMessage(t('collectionPage.alerts.importFailed'), { error: error.message }));
      }
    };
    input.click();
  };

  const handleDownloadBackupKey = () => {
    try {
      downloadBackupKeyFile();
      setShowSettingsMenu(false);
    } catch (error) {
      alert(formatMessage(t('collectionPage.alerts.genericError'), { error: error.message }));
    }
  };

  const handleCopyBackupKey = async () => {
    try {
      const formatted = await copyBackupKeyToClipboard();
      alert(formatMessage(t('collectionPage.alerts.copyKeySuccess'), { backupKey: formatted }));
      setShowSettingsMenu(false);
    } catch (error) {
      alert(formatMessage(t('collectionPage.alerts.genericError'), { error: error.message }));
    }
  };

  const handleDisconnectFromCloud = () => {
    const confirmMessage = t('collectionPage.prompts.disconnect');

    if (!window.confirm(confirmMessage)) {
      return;
    }

    const result = disconnectFromCloud();
    if (result.success) {
      setCloudSynced(false);
      setShowSettingsMenu(false);
      alert(t('collectionPage.alerts.disconnected'));
    } else {
      alert(formatMessage(t('collectionPage.alerts.disconnectFailed'), { error: result.error }));
    }
  };

  const markerCount = collection.length;
  const brandNames = Object.keys(groupedCollection).sort();
  const fadeClass = showContent ? 'opacity-100' : 'opacity-0';

  if (isLoading) {
    return (
      <div className="w-full py-16 flex flex-col items-center gap-4">
        <div
          className="w-12 h-12 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin"
          aria-hidden
        />
        <p className="text-slate-600 text-sm">{t('collectionPage.loading')}</p>
      </div>
    );
  }

  // Empty state
  if (markerCount === 0) {
    return (
      <>
        <div className={`max-w-full mx-auto px-4 sm:px-6 lg:px-8 space-y-8 transition-opacity duration-300 ease-out ${fadeClass}`}>
          <div className="text-center mb-8">
            <h1 className="text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-yellow-400 to-orange-500 inline-block px-3 rounded-lg">
              {t('collectionPage.title')}
            </h1>
            <FolderHeart size={28} className="inline-block ml-2 text-orange-500 pb-1" />
            <p className="text-slate-600">
              {t('collectionPage.subtitle')}
            </p>
            <PageHelpModal
              title={t('collectionPage.help.title')}
              tips={[
                { text: t('collectionPage.help.tip1') },
                { text: t('collectionPage.help.tip2') },
                { text: t('collectionPage.help.tip3') },
                { text: t('collectionPage.help.tip4') },
              ]}
              trigger={
                <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
                  {t('common.needHelp')}
                </p>
              }
            />
          </div>

          <div className="card-elegant p-12 text-center">
            {/* <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-yellow-100 to-orange-100 rounded-full flex items-center justify-center">
              <Plus size={48} className="text-orange-500" />
            </div> */}

            <h2 className="text-2xl font-bold text-slate-800 mb-4">
              {t('collectionPage.emptyState.heading')}
            </h2>

            <p className="text-slate-600 mb-6 max-w-md mx-auto">
              {t('collectionPage.emptyState.description')}
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary-green inline-flex items-center gap-2"
              >
                <Plus size={20} />
                {t('collectionPage.emptyState.addButton')}
              </button>

              {/* Restore Collection button with dropdown menu */}
              <div className="relative inline-flex">
                <button
                  onClick={() => setShowRestoreMenu(!showRestoreMenu)}
                  className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  <Settings size={18} />
                  {t('collectionPage.buttons.manage')}
                </button>

                {showRestoreMenu && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowRestoreMenu(false)}
                    />

                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                      {/* Show "Log In to Sync" for non-logged-in users */}
                      {!user && (
                        <button
                          onClick={async () => {
                            setShowRestoreMenu(false);
                            await login();
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                        >
                          <Cloud size={16} className="text-green-500" />
                          {t('collectionPage.menu.loginToSync')}
                        </button>
                      )}

                      <button
                        onClick={() => {
                          handleSyncFromCloud();
                          setShowRestoreMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                      >
                        <Download size={16} className="text-blue-500" />
                        {t('collectionPage.menu.restoreFromCloud')}
                      </button>

                      <div className="h-px bg-slate-200 my-2" />

                      <button
                        onClick={() => {
                          handleImport();
                          setShowRestoreMenu(false);
                        }}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                      >
                        <Upload size={16} className="text-slate-500" />
                        {t('collectionPage.menu.importJSON')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="mt-12 pt-8 border-t border-slate-200">
              <h3 className="font-semibold text-slate-700 mb-4">{t('collectionPage.whyBuild.title')}</h3>
              <div className="grid gap-3 text-left max-w-md mx-auto">
                <div className="flex items-start gap-3">
                  <span className="text-orange-500">✓</span>
                  <span className="text-sm text-slate-600">
                    {t('collectionPage.whyBuild.reason1')}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-orange-500">✓</span>
                  <span className="text-sm text-slate-600">
                    {t('collectionPage.whyBuild.reason3')}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-orange-500">✓</span>
                  <span className="text-sm text-slate-600">
                    {t('collectionPage.whyBuild.reason2')}
                  </span>
                </div>
                <div className="flex items-start gap-3">
                  <span className="text-orange-500">✓</span>
                  <span className="text-sm text-slate-600">
                    {t('collectionPage.whyBuild.reason4')}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Add Marker Modal */}
          {showAddModal && (
            <MarkerSearchModal
              isOpen={showAddModal}
              onClose={() => setShowAddModal(false)}
              brand={brand}
              ohuhuMode={ohuhuMode}
              onSelectMarker={handleAddMarker}
              onAddMarkers={handleAddMarkersBulk}
              enableBulkSelect
            />
          )}
        </div>
      </>
    );
  }

  // Collection view
  return (
    <>
      <div className={`w-full space-y-8 transition-opacity duration-300 ease-out ${fadeClass}`}>
        <div className="mb-8">
          {/* Header */}
          <div className="text-center">
            <h2 className="text-3xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-yellow-400 to-orange-500">
              {t('collectionPage.title')}
              <FolderHeart size={32} className="inline-block ml-2 text-orange-500 pb-1" />
            </h2>

            <p className="text-slate-600">
              {t('collectionPage.subtitle')}
            </p>
            <PageHelpModal
              title={t('collectionPage.help.title')}
              tips={[
                { text: t('collectionPage.help.tip1') },
                { text: t('collectionPage.help.tip2') },
                { text: t('collectionPage.help.tip3') },
                { text: t('collectionPage.help.tip4') },
              ]}
              trigger={
                <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
                  {t('common.needHelp')}
                </p>
              }
            />
          </div>

          {/* Expand nudge */}
          {showExpandNudge && (
            <div className="w-full flex items-center gap-2 px-3 py-2 my-2 rounded-xl bg-violet-50 text-left">
              <Sparkles size={14} className="text-violet-500 shrink-0" />
              <button
                onClick={() => { dismissExpandNudge(); navigate('/collection/expand'); }}
                className="flex-1 text-xs text-violet-700 text-left hover:text-violet-900 transition-colors"
              >
                <span className="font-semibold">Not sure what to buy next?</span> See which markers would add the most new colors to your collection.
              </button>
              <button
                onClick={dismissExpandNudge}
                className="shrink-0 p-0.5 rounded hover:bg-violet-200 transition-colors"
                aria-label="Dismiss"
              >
                <X size={13} className="text-violet-400" />
              </button>
            </div>
          )}

          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
            <div className="flex items-center gap-3 justify-end pb-4">
              {/* Cloud status */}
              {user ? (
                <SyncStatusIndicator status={syncStatus} />
              ) : cloudSynced ? (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-50">
                  <Cloud size={16} className="text-green-500" />
                  <span className="text-xs font-medium text-green-600">{t('collectionPage.cloudStatus.synced')}</span>
                </div>
              ) : (
                <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-slate-50">
                  <CloudOff size={16} className="text-slate-400" />
                  <span className="text-xs font-medium text-slate-500">{t('collectionPage.cloudStatus.localOnly')}</span>
                </div>
              )}

              {/* Expand collection */}
              <button
                onClick={() => { dismissExpandNudge(); navigate('/collection/expand'); }}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-violet-50 text-xs font-medium text-violet-600 hover:bg-violet-100 transition-colors"
              >
                <Sparkles size={16} />
                Expand
              </button>

              {/* Settings menu */}
              <div className="relative">
                <button
                  onClick={() => setShowSettingsMenu(!showSettingsMenu)}
                  className="inline-flex items-center px-2 py-1 rounded-full bg-slate-50 hover:bg-slate-100 text-slate-600 hover:text-slate-800 transition-colors"
                >
                  <Settings size={16} />
                </button>

                {showSettingsMenu && (
                  <>
                    {/* Backdrop */}
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowSettingsMenu(false)}
                    />

                    {/* Menu */}
                    <div className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-slate-200 py-2 z-50">
                      {/* Show "Log In to Sync" or backup key options for non-logged-in users */}
                      {!user && (
                        <>
                          <button
                            onClick={async () => {
                              setShowSettingsMenu(false);
                              await login();
                            }}
                            className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                          >
                            <Cloud size={16} className="text-green-500" />
                            {t('collectionPage.menu.loginToSync')}
                          </button>

                          {cloudSynced && (
                            <>
                              <button
                                onClick={handleDownloadBackupKey}
                                className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                              >
                                <Download size={16} className="text-slate-500" />
                                {t('collectionPage.menu.downloadKey')}
                              </button>

                              <button
                                onClick={handleCopyBackupKey}
                                className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                              >
                                <Copy size={16} className="text-slate-500" />
                                {t('collectionPage.menu.copyKey')}
                              </button>
                            </>
                          )}

                          <div className="h-px bg-slate-200 my-2" />
                        </>
                      )}

                      <button
                        onClick={handleExport}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                      >
                        <Download size={16} className="text-slate-500" />
                        {t('collectionPage.menu.exportJSON')}
                      </button>

                      <button
                        onClick={handleImport}
                        className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-3 text-sm"
                      >
                        <Upload size={16} className="text-slate-500" />
                        {t('collectionPage.menu.importJSON')}
                      </button>

                      <div className="h-px bg-slate-200 my-2" />

                      {/* Show disconnect option only for backup key users (not Google users) */}
                      {!user && cloudSynced && (
                        <button
                          onClick={handleDisconnectFromCloud}
                          className="w-full px-4 py-2 text-left hover:bg-yellow-50 flex items-center gap-3 text-sm text-yellow-700"
                        >
                          <CloudOff size={16} />
                          {t('collectionPage.menu.disconnectCloud')}
                        </button>
                      )}

                      <button
                        onClick={handleClearCollection}
                        className="w-full px-4 py-2 text-left hover:bg-red-50 flex items-center gap-3 text-sm text-red-600"
                      >
                        <Trash2 size={16} />
                        {t('collectionPage.menu.clearCollection')}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Search & Add */}
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
              <input
                type="text"
                placeholder={t('collectionPage.search.placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-9 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center justify-center px-3 py-2 text-xs font-medium border rounded-xl transition-all duration-200 bg-white/80 backdrop-blur-sm border-slate-200 text-slate-600 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300"
              title={t('collectionPage.buttons.addMarkers')}
            >
              <Plus size={14} />
            </button>
          </div>

          {/* Marker count */}
          <p className="text-slate-600 text-sm pt-4">
            {markerCount} {markerCount === 1 ? t('collectionPage.stats.marker') : t('collectionPage.stats.markers')} • {brandNames.length} {brandNames.length === 1 ? t('collectionPage.stats.brand') : t('collectionPage.stats.brands')}
          </p>

          {searchQuery && (
            <p className="text-sm text-slate-600 mt-2">
              {formatMessage(
                t('collectionPage.search.resultsFound'),
                {
                  count: filteredCollection.length,
                  label: filteredCollection.length === 1
                    ? t('collectionPage.stats.marker')
                    : t('collectionPage.stats.markers')
                }
              )}
            </p>
          )}
        </div>

        {/* Collection Grid (grouped by brand) */}
        <div className="space-y-6">
          {brandNames.map(brandName => (
            <div key={brandName}>
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-slate-700">
                  {brandName === 'Unknown' ? t('collectionPage.unknownBrand') : brandName} ({groupedCollection[brandName].length})
                </h2>
                <button
                  type="button"
                  onClick={() => handleRemoveBrand(brandName)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-red-500 hover:text-red-600"
                  title={t('collectionPage.buttons.remove')}
                  aria-label={t('collectionPage.buttons.remove')}
                >
                  <X size={18} />
                </button>
              </div>

              <div className="grid grid-cols-4 sm:grid-cols-6 lg:grid-cols-9 gap-2">
                {groupedCollection[brandName].map((marker, index) => (
                  <div key={`${marker.brand}-${marker.hex}-${index}`} className="relative group">
                    <ColorSwatch
                      color={marker}
                      ohuhuMode={ohuhuMode}
                      showLockIcons={false}
                      compact={true}
                    />
                    <button
                      onClick={() => handleRemoveMarker(marker)}
                      className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity shadow-lg hover:bg-red-600 flex items-center justify-center"
                      title={t('collectionPage.buttons.remove')}
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Help Notice */}
        <div className="mt-12 text-center">
          <p className="text-sm text-slate-500">
            {t('collectionPage.helpNotice.prefix')}
            <a
              href="mailto:markersync@gmail.com"
              className="text-blue-500 hover:text-blue-600 transition-colors"
            >
              markersync@gmail.com
            </a>
            {t('collectionPage.helpNotice.suffix')}
          </p>
        </div>

        {/* Add Marker Modal */}
        {showAddModal && (
          <MarkerSearchModal
            isOpen={showAddModal}
            onClose={() => setShowAddModal(false)}
            brand={brand}
            ohuhuMode={ohuhuMode}
            onSelectMarker={handleAddMarker}
            onAddMarkers={handleAddMarkersBulk}
            enableBulkSelect
          />
        )}
      </div>

      {/* Collection Conflict Modal (for Google auth users) */}
      <CollectionConflictModal
        isOpen={showConflictModal}
        onClose={() => setShowConflictModal(false)}
        onResolve={handleConflictResolve}
        localData={conflictData?.local ? {
          count: conflictData.local.count,
          timestamp: conflictData.local.timestamp
        } : null}
        cloudData={conflictData?.cloud ? {
          count: conflictData.cloud.count,
          timestamp: conflictData.cloud.timestamp
        } : null}
      />

      {/* Confirm Modal */}
      {ConfirmModalComponent}
    </>
  );
}
